/**
 * Roadmap Data Cache Strategy
 * 
 * Handles cache-first data fetching for roadmap macro, meso, and curriculum data.
 * Optimized for roadmap and curriculum offline-first requirements.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '@/services/offline/indexeddb';
import { BaseStrategy, type CacheStrategyOptions, type CacheStrategyResult } from './BaseStrategy';
import type { RoadmapMacroOffline, RoadmapMesoOffline, WeeklyReportOffline } from '@/services/offline/indexeddb/schema';
import { RoadmapMacroTransformer, RoadmapMesoTransformer } from '@/services/offline/sync/DataTransformer';

/**
 * Roadmap Data Strategy Implementation
 * 
 * Provides specialized caching strategies for roadmap data,
 * including macro and meso curriculum levels.
 */
export class RoadmapDataStrategy extends BaseStrategy {
  /**
   * Fetch roadmap macro data with cache-first strategy
   */
  async fetchRoadmapMacroCacheFirst(
    _options?: CacheStrategyOptions
  ): Promise<CacheStrategyResult<RoadmapMacroOffline[]>> {
    try {
      // Always try cache first
      const cachedData = await indexedDBService.roadmapMacro.getActive();

      // If we have cached data, return it
      if (cachedData && cachedData.length > 0) {
        return {
          data: cachedData,
          isStale: false,
          source: 'cache',
          timestamp: Date.now()
        };
      }

      // If cache is empty and we're online, fetch from network and cache it
      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('roadmap_macro')
          .select('*')
          .eq('is_active', true)
          .order('order_index');

        if (error) throw error;

        // Transform the data to offline format
        const roadmapData = (data || []).map(RoadmapMacroTransformer.fromSupabase);

        // Cache the data for future use
        if (roadmapData.length > 0) {
          await indexedDBService.offlineDB.putMany('roadmap_macro', roadmapData);
        }

        return {
          data: roadmapData,
          isStale: false,
          source: 'network',
          timestamp: Date.now()
        };
      }

      // If offline and no cache, return empty
      return {
        data: [],
        isStale: true,
        source: 'cache',
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('Failed to fetch roadmap macro data:', error);
      return {
        data: [],
        isStale: true,
        source: 'cache',
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch roadmap meso data with cache-first strategy
   */
  async fetchRoadmapMesoCacheFirst(
    _options?: CacheStrategyOptions
  ): Promise<CacheStrategyResult<RoadmapMesoOffline[]>> {
    try {
      // Always try cache first
      const cachedData = await indexedDBService.roadmapMeso.getActive();

      // If we have cached data, return it
      if (cachedData && cachedData.length > 0) {
        return {
          data: cachedData,
          isStale: false,
          source: 'cache',
          timestamp: Date.now()
        };
      }

      // If cache is empty and we're online, fetch from network and cache it
      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('roadmap_meso')
          .select('*')
          .eq('is_active', true)
          .order('order_index');

        if (error) throw error;

        // Transform the data to offline format
        const roadmapData = (data || []).map(RoadmapMesoTransformer.fromSupabase);

        // Cache the data for future use
        if (roadmapData.length > 0) {
          await indexedDBService.offlineDB.putMany('roadmap_meso', roadmapData);
        }

        return {
          data: roadmapData,
          isStale: false,
          source: 'network',
          timestamp: Date.now()
        };
      }

      // If offline and no cache, return empty
      return {
        data: [],
        isStale: true,
        source: 'cache',
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('Failed to fetch roadmap meso data:', error);
      return {
        data: [],
        isStale: true,
        source: 'cache',
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fetch roadmap data by belt level
   */
  async fetchRoadmapByBeltLevel(
    beltLevel: string,
    options?: CacheStrategyOptions
  ): Promise<CacheStrategyResult<{
    macro: RoadmapMacroOffline[];
    meso: RoadmapMesoOffline[];
  }>> {
    return this.execute(
      `roadmap_belt_${beltLevel}`,
      async () => {
        // Fetch both macro and meso data for the belt level
        const [macroResponse, mesoResponse] = await Promise.all([
          supabase
            .from('roadmap_macro')
            .select('*')
            .eq('is_active', true)
            .eq('belt_color', beltLevel)
            .order('order_index'),
          supabase
            .from('roadmap_meso')
            .select('*')
            .eq('is_active', true)
            .eq('belt_color', beltLevel)
            .order('order_index')
        ]);

        if (macroResponse.error) throw macroResponse.error;
        if (mesoResponse.error) throw mesoResponse.error;

        return {
          macro: (macroResponse.data || []).map(RoadmapMacroTransformer.fromSupabase),
          meso: (mesoResponse.data || []).map(RoadmapMesoTransformer.fromSupabase)
        };
      },
      async () => {
        const [macroData, mesoData] = await Promise.all([
          indexedDBService.roadmapMacro.getActive(),
          indexedDBService.roadmapMeso.getActive()
        ]);

        return {
          macro: macroData.filter(item => item.belt_color === beltLevel),
          meso: mesoData.filter(item => item.belt_color === beltLevel)
        };
      },
      options
    );
  }

  /**
   * Fetch weekly reports data
   */
  async fetchWeeklyReports(
    userId: string,
    options?: CacheStrategyOptions
  ): Promise<CacheStrategyResult<WeeklyReportOffline[]>> {
    return this.execute(
      `weekly_reports_${userId}`,
      async () => {
        const { data, error } = await supabase
          .from('weekly_reports')
          .select('*')
          .eq('user_id', userId)
          .order('week_start', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      () => indexedDBService.weeklyReports.getByUserId(userId),
      options
    );
  }

  /**
   * Get roadmap progress statistics
   */
  async getRoadmapStats(_userId: string): Promise<{
    currentBeltLevel: string;
    macroProgress: number;
    mesoProgress: number;
    completedMacros: number;
    completedMesos: number;
    totalMacros: number;
    totalMesos: number;
  }> {
    try {
      const [userProfile, macroData, mesoData] = await Promise.all([
        indexedDBService.userProfile.getCurrentProfile(_userId),
        indexedDBService.roadmapMacro.getActive(),
        indexedDBService.roadmapMeso.getActive()
      ]);

      const currentBeltLevel = userProfile?.belt_rank || 'white';

      // Filter by current belt level
      const currentMacros = macroData.filter((item: RoadmapMacroOffline) => item.belt_color === currentBeltLevel);
      const currentMesos = mesoData.filter((item: RoadmapMesoOffline) => item.belt_color === currentBeltLevel);

      // Calculate progress (this would need actual progress tracking)
      const completedMacros = 0; // Placeholder - would need progress data
      const completedMesos = 0; // Placeholder - would need progress data

      const macroProgress = currentMacros.length > 0 ? (completedMacros / currentMacros.length) * 100 : 0;
      const mesoProgress = currentMesos.length > 0 ? (completedMesos / currentMesos.length) * 100 : 0;

      return {
        currentBeltLevel,
        macroProgress,
        mesoProgress,
        completedMacros,
        completedMesos,
        totalMacros: currentMacros.length,
        totalMesos: currentMesos.length
      };
    } catch (error) {
      console.warn('Failed to calculate roadmap stats:', error);
      return {
        currentBeltLevel: 'white',
        macroProgress: 0,
        mesoProgress: 0,
        completedMacros: 0,
        completedMesos: 0,
        totalMacros: 0,
        totalMesos: 0
      };
    }
  }

  /**
   * Get available belt levels
   */
  async getBeltLevels(): Promise<string[]> {
    try {
      const macroData = await indexedDBService.roadmapMacro.getActive();
      const beltLevels = [...new Set(macroData.map(item => item.belt_color))];
      
      // Sort belt levels in progression order
      const beltOrder = ['white', 'blue', 'purple', 'brown', 'black'];
      return beltLevels.sort((a, b) => {
        const indexA = beltOrder.indexOf(a);
        const indexB = beltOrder.indexOf(b);
        return indexA - indexB;
      });
    } catch (error) {
      console.warn('Failed to get belt levels:', error);
      return ['white', 'blue', 'purple', 'brown', 'black'];
    }
  }
}

// Export singleton instance
export const roadmapDataStrategy = new RoadmapDataStrategy();

/**
 * Roadmap Macro cache-first fetcher
 */
export async function fetchRoadmapMacroCacheFirst(
  options?: CacheStrategyOptions
): Promise<CacheStrategyResult<RoadmapMacroOffline[]>> {
  return roadmapDataStrategy.fetchRoadmapMacroCacheFirst(options);
}

/**
 * Roadmap Meso cache-first fetcher
 */
export async function fetchRoadmapMesoCacheFirst(
  options?: CacheStrategyOptions
): Promise<CacheStrategyResult<RoadmapMesoOffline[]>> {
  return roadmapDataStrategy.fetchRoadmapMesoCacheFirst(options);
}

/**
 * Roadmap by belt level cache-first fetcher
 */
export async function fetchRoadmapByBeltLevel(
  beltLevel: string,
  options?: CacheStrategyOptions
): Promise<CacheStrategyResult<{
  macro: RoadmapMacroOffline[];
  meso: RoadmapMesoOffline[];
}>> {
  return roadmapDataStrategy.fetchRoadmapByBeltLevel(beltLevel, options);
}
